import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import Menu, { MenuProps } from 'components/menu'
import { ChevronDown, ChevronUp, Csv, Lock, Plus, Spreadsheet } from 'components/icons'
import {
  INITIAL_CONTEXT_MENU_STATE,
  IntegrationProviders,
  IntegrationSyncResourceTypes,
  ORG_PLAN_TIERS,
  SYNC_CONFIGURATION_RESTRICTED_MESSAGE,
  TEAM_SCALE_PLAN
} from 'app-constants'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { updateTable } from 'components/spreadsheet/contexts/data/actions'
import Select from 'components/select'
import { useApplicationStore } from 'hooks/application'
import useProject from 'hooks/useProject'
import Button from 'components/button'
import api from 'helpers/api'
import Flag from 'components/flag'
import { capitaliseEachWord, validateSourceUrl } from 'helpers/utils'
import { IIntegrationCatalogue, IIntegrationRedirects } from 'types'
import { useAuth } from 'hooks/auth'
import { isAccountConnected } from 'helpers/integration'
import { INTEGRATIONS } from 'routes'
import Skeleton from 'components/skeleton'
import { logoClass } from './logos'
import Tooltip from 'components/tooltip'

const Active: React.FC<{ active: boolean }> = ({ active }) => (
  <span
    // The ink has to follow the fill: white reads on the red but not on the
    // green, which is light in both themes.
    className={`${
      active ? 'bg-green text-on-light' : 'bg-red text-on-solid'
    } rounded-full font-bold text-sm px-2.5 py-1 text-[90%]`}
  >
    {active ? 'Active' : 'Inactive'}
  </span>
)

const ScalePlan = () => (
  <span className={`bg-blue rounded font-bold text-sm p-[5px] mr-2.5 text-on-solid text-[90%]`}>{TEAM_SCALE_PLAN}</span>
)

type ResourceConfig = {
  name: string
  type: IntegrationSyncResourceTypes
  icon: string
  open: () => void
  flag?: string
  disabled?: boolean
}

type ProviderConfig = {
  key: string
  display: string
  provider: IntegrationProviders
  icon: string
  resources: ResourceConfig[]
}

interface ImportMenuProps extends MenuProps {
  openIntegration: (provider: IntegrationProviders, syncType: IntegrationSyncResourceTypes, name: string) => void
  setAppendFileModal: (v: boolean) => void
  setImportFromTableModal: (v: boolean) => void
}

const ImportMenu: React.FC<ImportMenuProps> = (props) => {
  const { user } = useAuth()
  const { id, menuState, setMenuState, width } = props
  const { spreadsheetData, setSpreadsheetData } = useDataContext()
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const { project } = useProject()
  const navigate = useNavigate()
  // Where the organisation only lets Site Admins configure syncs, the controls stay visible but disabled, saying why
  const canConfigureSync = project.canConfigureSync !== false

  const table = spreadsheetData.tableDetails
  const isTableSynced = table.isSynced

  const [loadingCatalogue, setLoadingCatalogue] = useState(false)
  const [catalogue, setCatalogue] = useState<IIntegrationCatalogue>({})
  const [integrationRedirects, setIntegrationRedirects] = useState<IIntegrationRedirects>({})

  useEffect(() => {
    setLoadingCatalogue(true)
    api
      .getIntegrationCatalogue()
      .then((res) => setCatalogue(res.data))
      .catch((e) => displayErrorMessage(e))
      .finally(() => setLoadingCatalogue(false))
  }, [])

  useEffect(() => {
    api
      .getIntegrationRedirects()
      .then((res) => setIntegrationRedirects(res.data))
      .catch((e) => displayErrorMessage(e))
  }, [])

  const providers: ProviderConfig[] = Object.entries(catalogue)
    .map(([provider, info]) => {
      const disabledSet = new Set(info.disabledTypes || [])
      const resources: ResourceConfig[] = info.types
        .map((t) => ({
          name: capitaliseEachWord(t.replace(/_/g, ' ')) || t,
          type: t as IntegrationSyncResourceTypes,
          icon: info.icon,
          open: () => {
            props.openIntegration(provider as IntegrationProviders, t as IntegrationSyncResourceTypes, info.display)
            setMenuState(INITIAL_CONTEXT_MENU_STATE)
          },
          flag: undefined,
          disabled: disabledSet.has(t)
        }))
        .sort((a, b) => a.name.localeCompare(b.name))

      return {
        key: provider.toUpperCase(),
        display: info.display,
        provider: provider as IntegrationProviders,
        icon: info.icon,
        resources
      }
    })
    .sort((a, b) => a.display.localeCompare(b.display))

  const [expanded, setExpanded] = useState<Record<string, boolean>>({})

  useEffect(() => {
    setExpanded(Object.fromEntries(providers.map((p) => [p.key, table.isSynced && table.syncProvider === p.provider])))
  }, [providers, table.isSynced, table.syncProvider])
  // Accordion behaviour: opening one integration's dropdown closes any other that is open, keeping
  // the menu tidy and easy to scan.
  const toggleExpand = (key: string) => setExpanded((prev) => (prev[key] ? {} : { [key]: true }))

  // When a sync is active, surface it (and its active sync type) in the External Import Settings
  // block rather than mixed in with the connectable data sources.
  const syncedProvider = isTableSynced ? providers.find((p) => p.provider === table.syncProvider) : undefined
  const activeResource = syncedProvider?.resources.find((r) => r.type === table.syncType)

  // Split the remaining catalogue into accounts the user has connected and everything else. Morta
  // is always treated as connected (see isAccountConnected), so it stays in the connected group.
  const connectedProviders = providers.filter(
    (p) => isAccountConnected(user, p.provider) && p.key !== syncedProvider?.key
  )
  const notConnectedProviders = providers.filter(
    (p) => !isAccountConnected(user, p.provider) && p.key !== syncedProvider?.key
  )

  const [showMore, setShowMore] = useState(false)
  const [showOtherConnected, setShowOtherConnected] = useState(false)

  const goToConnect = (providerConfig: ProviderConfig) => {
    const redirectUrl = integrationRedirects[providerConfig.provider]
    // OAuth providers start their connect flow directly from their redirect URL. API-key providers
    // (modal) and Revizto (region select) can't be launched by a plain redirect, so fall back to the
    // Integrations settings page.
    if (redirectUrl && providerConfig.provider !== IntegrationProviders.REVIZTO) {
      window.location.assign(redirectUrl)
      return
    }
    navigate(INTEGRATIONS)
    setMenuState(INITIAL_CONTEXT_MENU_STATE)
  }

  const renderResource = (providerConfig: ProviderConfig, r: ResourceConfig) => {
    const providerIsThisTable = isTableSynced && table.syncProvider === providerConfig.provider
    const active = providerIsThisTable && table.syncType === r.type
    const comingSoon = r.disabled && !active
    const syncLocked = isTableSynced && !active
    const restricted = !canConfigureSync && !comingSoon && !syncLocked
    const disabled = comingSoon || syncLocked || restricted

    const handleSync = () => {
      if (disabled) {
        return
      }
      if (
        r.type === IntegrationSyncResourceTypes.VIEWS &&
        (!user || !user.team || user.team.orgPlanTier === ORG_PLAN_TIERS.PROFESSIONAL)
      ) {
        setSnackbarMessage({
          status: 'subscription',
          message:
            'Your organisation needs to be on a Team or Scale Up plan to access this feature. Please contact your organisation administrator or mo@morta.io.'
        })
        return
      }
      r.open()
    }

    const row = (
      <div
        className={`relative flex items-center rounded text-body truncate hover:bg-surface-sunken h-8 leading-[32px] py-0 px-2.5 my-0 mx-2.5 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${disabled ? 'opacity-50' : ''}`}
        onClick={handleSync}
        aria-disabled={disabled || undefined}
      >
        <img src={validateSourceUrl(r.icon)} className={logoClass(r.icon)} alt={`${r.name}`} />
        <span className="flex ml-2.5 mr-auto">
          {r.name}
          {r.flag && <Flag text={r.flag} color="var(--color-red)" />}
          {r.disabled && !active && <Flag text="Coming Soon" color="var(--color-blue)" />}
        </span>
        {r.type === IntegrationSyncResourceTypes.VIEWS &&
          user?.team?.orgPlanTier !== ORG_PLAN_TIERS.TEAM &&
          user?.team?.orgPlanTier !== ORG_PLAN_TIERS.SCALE_UP && <ScalePlan />}
        {!disabled && (
          <Button
            internalType="accept"
            className="h-auto text-[80%] gap-1 py-[3px] px-2"
            data-testid={`import-sync-button-${r.type}`}
            onClick={(e) => {
              e.stopPropagation()
              handleSync()
            }}
          >
            <Plus className="w-3 h-3" />
            Import
          </Button>
        )}
      </div>
    )

    return (
      <li className="mt-[5px]" key={r.type} data-testid={`import-resource-${r.type}`}>
        {restricted ? <Tooltip content={SYNC_CONFIGURATION_RESTRICTED_MESSAGE}>{row}</Tooltip> : row}
      </li>
    )
  }

  const handleManualRetrySync = () => {
    if (!table.isSynced) {
      setSnackbarMessage({
        message: "This table isn't synced from another app, so there's no sync to retry.",
        status: 'error'
      })
      return
    }
    api
      .manualSyncIntegration(table.publicId)
      .then(() => {
        setSnackbarMessage({
          message: 'Import retried successfully. You will receive an email shortly.',
          status: 'success'
        })
        setSpreadsheetData({ type: 'UPDATE_TABLE', field: 'isSyncing', value: true })
        const interval = setInterval(() => {
          api.getSyncIntegrationInfo(table.publicId).then((res) => {
            if (res?.data?.isSyncing === false) {
              setSpreadsheetData({ type: 'UPDATE_TABLE', field: 'isSyncing', value: false })
              setSpreadsheetData({ type: 'UPDATE_TABLE', field: 'lastSync', value: res.data.lastSync })
              setSpreadsheetData({
                type: 'UPDATE_TABLE',
                field: 'failedSyncAttempts',
                value: res.data.failedSyncAttempts
              })
              clearInterval(interval)
            }
          })
        }, 3000)
      })
      .catch(displayErrorMessage)
  }

  // A connected account with its sync resource types. Every account is expandable so users can see
  // what each integration offers; while a sync is active those types are all locked (greyed,
  // non-clickable), and the synced integration additionally hides its active type (shown in External
  // Import Settings).
  const renderConnectedProvider = (provider: ProviderConfig) => {
    const isSyncedProvider = isTableSynced && provider.provider === table.syncProvider
    const resources = isSyncedProvider
      ? provider.resources.filter((r) => r.type !== table.syncType)
      : provider.resources
    const expandable = resources.length > 0
    const isExpanded = expandable && expanded[provider.key]
    return (
      <div key={provider.key} data-testid={`import-provider-${provider.key}`}>
        <li
          className={`flex items-center h-12 ${isExpanded ? 'bg-surface-strong' : ''} px-5 ${expandable ? 'cursor-pointer hover:bg-surface-sunken' : ''}`}
          data-testid={`import-provider-toggle-${provider.key}`}
          onClick={expandable ? () => toggleExpand(provider.key) : undefined}
        >
          <img
            src={validateSourceUrl(provider.icon)}
            className={logoClass(provider.icon)}
            alt={`${provider.display}`}
          />
          <span className="ml-2.5">{provider.display}</span>
          {expandable && <span className="ml-auto">{isExpanded ? <ChevronUp /> : <ChevronDown />}</span>}
        </li>

        {isExpanded && (
          <div className="bg-surface-sunken py-2.5 px-0">{resources.map((r) => renderResource(provider, r))}</div>
        )}
      </div>
    )
  }

  // Read-only preview of a sync you would unlock once the account is connected. Uses the same icon
  // and spacing as a connected account's sync rows so the two lists read identically.
  const renderPreviewResource = (r: ResourceConfig) => (
    <li className="mt-[5px]" key={r.type} data-testid={`import-preview-resource-${r.type}`}>
      <div className="relative flex items-center rounded text-body truncate h-8 leading-[32px] py-0 px-2.5 my-0 mx-2.5">
        <img src={validateSourceUrl(r.icon)} className={logoClass(r.icon)} alt={`${r.name}`} />
        <span className="flex ml-2.5 mr-auto">{r.name}</span>
      </div>
    </li>
  )

  // A not-connected account. The row carries a Connect call-to-action to the left of the chevron; the
  // expanded preview lists the syncs it offers and closes with a prompt to connect the account.
  const renderConnectProvider = (provider: ProviderConfig) => {
    const isExpanded = expanded[provider.key]
    return (
      <div key={provider.key} data-testid={`import-connect-provider-${provider.key}`}>
        <li
          className={`flex items-center h-12 ${isExpanded ? 'bg-surface-strong' : ''} px-5 cursor-pointer hover:bg-surface-sunken`}
          data-testid={`import-connect-toggle-${provider.key}`}
          onClick={() => toggleExpand(provider.key)}
        >
          <img
            src={validateSourceUrl(provider.icon)}
            className={logoClass(provider.icon)}
            alt={`${provider.display}`}
          />
          <span className="ml-2.5 mr-auto">{provider.display}</span>
          <Button
            internalType="secondary"
            className="h-auto text-[80%] gap-1 py-[3px] px-2"
            data-testid={`import-connect-button-${provider.key}`}
            onClick={(e) => {
              e.stopPropagation()
              goToConnect(provider)
            }}
          >
            <Plus className="w-3 h-3" />
            Connect
          </Button>
          <span className="ml-2.5">{isExpanded ? <ChevronUp /> : <ChevronDown />}</span>
        </li>

        {isExpanded && (
          <div className="bg-surface-sunken py-2.5 px-0">
            {provider.resources.map((r) => renderPreviewResource(r))}
            <li className="mt-2.5">
              <div className="flex items-center text-body opacity-60 text-[90%] py-0 px-2.5 my-0 mx-2.5">
                <Lock />
                <span className="ml-2.5">Connect your {provider.display} account to sync these.</span>
              </div>
            </li>
          </div>
        )}
      </div>
    )
  }

  const autoRefreshSelect = (
    <Select
      disabled={!canConfigureSync}
      options={[
        { label: 'Never', value: '0' },
        { label: 'Every Day (Midnight)', value: '24' }
      ]}
      onOptionClick={(option) => {
        updateTable(
          'syncHourlyFrequency',
          spreadsheetData,
          setSpreadsheetData,
          Number.parseInt(option),
          project.publicId,
          () =>
            setSnackbarMessage({
              message: 'Sync frequency updated successfully',
              status: 'success'
            }),
          (error) => displayErrorMessage(error)
        )
      }}
      optionsSelected={[spreadsheetData.tableDetails.syncHourlyFrequency.toString()]}
      searchable={false}
    />
  )

  return (
    <Menu id={id} menuState={menuState} setMenuState={setMenuState} width={width} zIndex={3000}>
      <div className="overflow-y-auto p-0 max-h-[500px] list-none">
        {!spreadsheetData.viewDetails.disableNewRow && !spreadsheetData.viewDetails.disableSyncCsv && (
          <>
            <li
              data-tour="import-from-file"
              className={`relative flex items-center rounded text-body select-none transition-all truncate hover:bg-surface-sunken m-2.5 h-8 leading-[32px] py-0 px-2.5 ${spreadsheetData.tableDetails.isSynced ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              onClick={() => {
                if (spreadsheetData.tableDetails.isSynced) {
                  return
                }
                props.setAppendFileModal(true)
                setMenuState(INITIAL_CONTEXT_MENU_STATE)
              }}
            >
              <Csv />
              <span className="ml-2.5">Import From File (CSV / Excel)</span>
            </li>

            <li
              className={`relative flex items-center rounded text-body select-none transition-all truncate hover:bg-surface-sunken m-2.5 h-8 leading-[32px] py-0 px-2.5 ${spreadsheetData.tableDetails.isSynced ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              onClick={() => {
                if (spreadsheetData.tableDetails.isSynced) {
                  return
                }
                props.setImportFromTableModal(true)
                setMenuState(INITIAL_CONTEXT_MENU_STATE)
              }}
            >
              <Spreadsheet />
              <span className="ml-2.5">Import Direct From Morta Table View</span>
            </li>
          </>
        )}

        {spreadsheetData.tableDetails.isSynced && (
          <div>
            <li
              data-tour="sync-settings-header"
              className="font-bold bg-surface-sunken py-2.5 px-5 border-t border-divider"
            >
              External Import Settings
            </li>

            {syncedProvider && (
              <li data-testid="import-current-source" className="mt-[5px]">
                <div className="relative flex items-center rounded text-body select-none m-2.5 h-8 leading-[32px] py-0 px-2.5">
                  <span>Import Source:</span>
                  <div className="ml-auto flex items-center">
                    <img
                      src={validateSourceUrl(syncedProvider.icon)}
                      className={logoClass(syncedProvider.icon)}
                      alt={`${syncedProvider.display}`}
                    />
                    <span className="ml-2 mr-2.5">
                      {syncedProvider.display}
                      {activeResource ? ` · ${activeResource.name}` : ''}
                    </span>
                    <Active active />
                  </div>
                </div>
              </li>
            )}

            {syncedProvider && activeResource && spreadsheetData.isAdmin && (
              <li className="mt-2.5">
                <div className="relative flex items-center rounded text-body select-none m-2.5 h-8 leading-[32px] py-0 px-2.5">
                  <span className="mr-[5px]">Update or Remove:</span>
                  {canConfigureSync ? (
                    <Button
                      data-testid="import-update-remove"
                      internalType="outline"
                      className="ml-auto w-[200px]"
                      onClick={() => activeResource?.open()}
                    >
                      Update or remove
                    </Button>
                  ) : (
                    // A disabled button gets no hover events, so the wrapper carries the tooltip
                    <Tooltip content={SYNC_CONFIGURATION_RESTRICTED_MESSAGE}>
                      <span className="ml-auto" data-testid="import-update-remove-restricted">
                        <Button internalType="outline" className="w-[200px]" disabled>
                          Update or remove
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </div>
              </li>
            )}

            <li data-tour="sync-last-successful" className="mt-[5px]">
              <div className="relative flex items-center rounded text-body select-none m-2.5 h-8 leading-[32px] py-0 px-2.5">
                <span>Last Successful Import:</span>
                <div className="ml-auto">
                  {spreadsheetData.tableDetails.isSynced && spreadsheetData.tableDetails.lastSync
                    ? new Date(spreadsheetData.tableDetails.lastSync + 'Z').toLocaleString()
                    : 'n/a'}
                </div>
              </div>
            </li>

            {spreadsheetData.tableDetails.failedSyncAttempts > 0 && (
              <div>
                <li className="mt-2.5">
                  <div className="relative flex items-center rounded text-body select-none m-2.5 h-8 leading-[32px] py-0 px-2.5">
                    <span className="mr-[5px]">
                      <b className="text-red">{spreadsheetData.tableDetails.failedSyncAttempts} Failed</b> Import
                      Attempts
                    </span>
                  </div>
                </li>
              </div>
            )}

            {spreadsheetData.isAdmin && (
              <li data-tour="sync-auto-refresh" className="mt-[5px]">
                <div className="relative flex items-center rounded text-body select-none m-2.5 py-0 px-2.5">
                  <span>Auto Refresh:</span>
                  <div className="ml-auto w-[200px]">
                    {canConfigureSync ? (
                      autoRefreshSelect
                    ) : (
                      <Tooltip content={SYNC_CONFIGURATION_RESTRICTED_MESSAGE}>
                        <div data-testid="import-auto-refresh-restricted">{autoRefreshSelect}</div>
                      </Tooltip>
                    )}
                  </div>
                </div>
              </li>
            )}

            <div>
              <li className="mt-2.5">
                <div className="relative flex items-center rounded text-body select-none m-2.5 h-8 leading-[32px] py-0 px-2.5">
                  <span className="mr-[5px]">Manual Refresh:</span>
                  <Button
                    data-tour="sync-now-button"
                    internalType="outline"
                    onClick={() => {
                      handleManualRetrySync()
                    }}
                    className="ml-auto w-[200px]"
                    isLoading={spreadsheetData.tableDetails.isSyncing}
                  >
                    Import Now
                  </Button>
                </div>
              </li>
            </div>
          </div>
        )}

        {spreadsheetData.isAdmin && (
          <div>
            {!isTableSynced && (
              <li className="flex items-center font-bold bg-surface-sunken py-2.5 px-5 border-t border-divider">
                External Data Sources
              </li>
            )}

            {loadingCatalogue && (
              <div>
                <li className="flex items-center py-2.5 px-5">
                  <Skeleton className="w-full h-[20.5px]" />
                </li>
                <li className="flex items-center py-2.5 px-5">
                  <Skeleton className="w-full h-[20.5px]" />
                </li>
                <li className="flex items-center py-2.5 px-5">
                  <Skeleton className="w-full h-[20.5px]" />
                </li>
              </div>
            )}

            {!loadingCatalogue && (
              <>
                {syncedProvider ? (
                  <>
                    <li
                      className="flex items-center bg-surface-sunken cursor-pointer py-2.5 px-5 border-t border-divider"
                      data-testid="import-other-connected"
                      onClick={() => setShowOtherConnected((prev) => !prev)}
                    >
                      <span className="font-bold">Other connected accounts</span>
                      <span className="ml-2.5 text-muted text-[90%]">
                        {connectedProviders.length + 1} {connectedProviders.length + 1 === 1 ? 'account' : 'accounts'}
                      </span>
                      <span className="ml-auto">{showOtherConnected ? <ChevronUp /> : <ChevronDown />}</span>
                    </li>

                    {showOtherConnected && (
                      <>
                        <li className="text-muted text-[90%] py-2 px-5" data-testid="import-other-connected-hint">
                          Remove the current import to set up a new connection.
                        </li>
                        {renderConnectedProvider(syncedProvider)}
                        {connectedProviders.map((provider) => renderConnectedProvider(provider))}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <li className="text-muted text-[90%] py-2 px-5">Your connected accounts</li>

                    {connectedProviders.length === 0 && (
                      <li className="text-body py-2.5 px-5" data-testid="import-connected-empty">
                        You have no connected accounts. Connect one below to start syncing data into this table.
                      </li>
                    )}

                    {connectedProviders.map((provider) => renderConnectedProvider(provider))}
                  </>
                )}

                {notConnectedProviders.length > 0 && (
                  <>
                    <li
                      className="flex items-center bg-surface-sunken cursor-pointer py-2.5 px-5 border-t border-divider"
                      data-testid="import-connect-more"
                      onClick={() => setShowMore((prev) => !prev)}
                    >
                      <span className="font-bold">Connect more accounts</span>
                      <span className="ml-2.5 text-muted text-[90%]">
                        {notConnectedProviders.length} more{' '}
                        {notConnectedProviders.length === 1 ? 'integration' : 'integrations'} available
                      </span>
                      <span className="ml-auto">{showMore ? <ChevronUp /> : <ChevronDown />}</span>
                    </li>

                    {showMore && notConnectedProviders.map((provider) => renderConnectProvider(provider))}
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </Menu>
  )
}

export default ImportMenu
